import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './lib/authStore';
import { ProtectedRoute } from './components/common/ProtectedRoute';

// Layouts
import { PublicLayout } from './components/layout/PublicLayout';
import { ClientLayout } from './components/layout/ClientLayout';
import { AdminLayout } from './components/layout/AdminLayout';

// Public Pages
import { HomePage } from './pages/public/HomePage';
import { AboutPage } from './pages/public/AboutPage';
import { ServicesPage } from './pages/public/ServicesPage';
import { ServiceDetailPage } from './pages/public/ServiceDetailPage';
import { AuthorPage } from './pages/public/AuthorPage';
import { ProcessPage } from './pages/public/ProcessPage';
import { PublicationsPage } from './pages/public/PublicationsPage';
import { PublicationDetailPage } from './pages/public/PublicationDetailPage';
import { ResourcesPage } from './pages/public/ResourcesPage';
import { ResourceDetailPage } from './pages/public/ResourceDetailPage';
import { NewsPage } from './pages/public/NewsPage';
import { NewsDetailPage } from './pages/public/NewsDetailPage';
import { ContactPage } from './pages/public/ContactPage';
import { EnquiryPage } from './pages/public/EnquiryPage';
import { LoginPage } from './pages/public/LoginPage';
import { RegisterPage } from './pages/public/RegisterPage';
import { LegalTermsPage } from './pages/public/LegalTermsPage';
import { NotFoundPage } from './pages/public/NotFoundPage';

// Client Portal Pages
import { ClientDashboardPage } from './pages/client/ClientDashboardPage';
import { ClientProjectsPage } from './pages/client/ClientProjectsPage';
import { ClientProjectDetailPage } from './pages/client/ClientProjectDetailPage';
import { ClientTranslationsPage } from './pages/client/ClientTranslationsPage';
import { ClientInvoicesPage } from './pages/client/ClientInvoicesPage';
import { ClientDocumentsPage } from './pages/client/ClientDocumentsPage';
import { ClientMessagesPage } from './pages/client/ClientMessagesPage';
import { ClientSettingsPage } from './pages/client/ClientSettingsPage';

// Admin Portal Pages
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { AdminEnquiriesPage } from './pages/admin/AdminEnquiriesPage';
import { AdminEnquiryDetailPage } from './pages/admin/AdminEnquiryDetailPage';
import { AdminPartnersPage } from './pages/admin/AdminPartnersPage';
import { AdminCustomersPage } from './pages/admin/AdminCustomersPage';
import { AdminTranslationsPage } from './pages/admin/AdminTranslationsPage';
import { AdminRevenuePage } from './pages/admin/AdminRevenuePage';
import { AdminStaffPage } from './pages/admin/AdminStaffPage';
import { AdminClientsPage } from './pages/admin/AdminClientsPage';
import { AdminProjectsPage } from './pages/admin/AdminProjectsPage';
import { AdminDocumentsPage } from './pages/admin/AdminDocumentsPage';
import { AdminMessagesPage } from './pages/admin/AdminMessagesPage';
import { AdminCmsServicesPage } from './pages/admin/AdminCmsServicesPage';
import { AdminCmsPublicationsPage } from './pages/admin/AdminCmsPublicationsPage';
import { AdminDomainsPage } from './pages/admin/AdminDomainsPage';
import { AdminActivityLogsPage } from './pages/admin/AdminActivityLogsPage';
import { AdminLoginPage } from './pages/admin/AdminLoginPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30000,
    },
  },
});

export const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public Layout Routes */}
            <Route path="/" element={<PublicLayout />}>
              <Route index element={<HomePage />} />
              <Route path="about" element={<AboutPage />} />
              <Route path="services" element={<ServicesPage />} />
              <Route path="services/:slug" element={<ServiceDetailPage />} />
              <Route path="for-authors" element={<AuthorPage />} />
              <Route path="our-process" element={<ProcessPage />} />
              <Route path="publications" element={<PublicationsPage />} />
              <Route path="publications/:slug" element={<PublicationDetailPage />} />
              <Route path="resources" element={<ResourcesPage />} />
              <Route path="resources/:slug" element={<ResourceDetailPage />} />
              <Route path="news" element={<NewsPage />} />
              <Route path="news/:slug" element={<NewsDetailPage />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="submit-enquiry" element={<EnquiryPage />} />
              <Route path="enquiry" element={<EnquiryPage />} />
              <Route path="login" element={<LoginPage />} />
              <Route path="register" element={<RegisterPage />} />
              <Route path="legal" element={<LegalTermsPage />} />
            </Route>

            {/* Client Portal Routes (Protected) */}
            <Route
              path="/client"
              element={
                <ProtectedRoute allowedRoles={['CLIENT', 'SUPER_ADMIN', 'ADMIN', 'EDITOR']}>
                  <ClientLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/client/dashboard" replace />} />
              <Route path="dashboard" element={<ClientDashboardPage />} />
              <Route path="projects" element={<ClientProjectsPage />} />
              <Route path="projects/:id" element={<ClientProjectDetailPage />} />
              <Route path="translations" element={<ClientTranslationsPage />} />
              <Route path="invoices" element={<ClientInvoicesPage />} />
              <Route path="documents" element={<ClientDocumentsPage />} />
              <Route path="messages" element={<ClientMessagesPage />} />
              <Route path="settings" element={<ClientSettingsPage />} />
            </Route>

            {/* Dedicated Admin Login Page (Google One-Click) */}
            <Route path="/admin/login" element={<AdminLoginPage />} />

            {/* Admin Backend Routes (Protected for Staff / Admins) */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN', 'EDITOR']}>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboardPage />} />
              <Route path="enquiries" element={<AdminEnquiriesPage />} />
              <Route path="enquiries/:id" element={<AdminEnquiryDetailPage />} />
              <Route path="partners" element={<AdminPartnersPage />} />
              <Route path="customers" element={<AdminCustomersPage />} />
              <Route path="translations" element={<AdminTranslationsPage />} />
              <Route path="revenue" element={<AdminRevenuePage />} />
              <Route path="staff" element={<AdminStaffPage />} />
              <Route path="clients" element={<AdminClientsPage />} />
              <Route path="projects" element={<AdminProjectsPage />} />
              <Route path="documents" element={<AdminDocumentsPage />} />
              <Route path="messages" element={<AdminMessagesPage />} />
              <Route path="cms/services" element={<AdminCmsServicesPage />} />
              <Route path="cms/publications" element={<AdminCmsPublicationsPage />} />
              <Route path="domains" element={<AdminDomainsPage />} />
              <Route path="activity-logs" element={<AdminActivityLogsPage />} />
            </Route>

            {/* 404 Catch-All */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
